/**
 * useTransactions Hook
 *
 * 거래 목록을 조회하고 필터링하는 Hook입니다.
 */

import { useState, useEffect } from 'react'
import { transactionRepository } from '../repositories'
import { logger } from '@/core/logger'
import type { Transaction, TransactionFilter } from '../types/Transaction'

interface UseTransactionsReturn {
  transactions: Transaction[]
  loading: boolean
  error: Error | null
  refetch: () => Promise<void>
}

/**
 * 거래 목록 조회 Hook
 *
 * @param filter - 필터 조건 (선택사항)
 * @returns 거래 목록, 로딩 상태, 에러, refetch 함수
 *
 * @example
 * ```tsx
 * function TransactionList() {
 *   const { transactions, loading, error } = useTransactions({
 *     startDate: '2025-12-01',
 *     endDate: '2025-12-31'
 *   })
 *
 *   if (loading) return <div>로딩 중...</div>
 *   if (error) return <div>에러: {error.message}</div>
 *
 *   return (
 *     <ul>
 *       {transactions.map(tx => (
 *         <li key={tx.id}>{tx.memo}: {tx.amount}원</li>
 *       ))}
 *     </ul>
 *   )
 * }
 * ```
 */
export function useTransactions(
  filter?: TransactionFilter
): UseTransactionsReturn {
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchTransactions = async () => {
    try {
      setLoading(true)
      setError(null)

      // 성능 모니터링: 로딩 시작 시간 기록
      const loadStart = performance.now()

      const data = await transactionRepository.findAll(filter)
      setTransactions(data)

      // 성능 모니터링: 로딩 시간 계산 및 로그
      const loadTime = performance.now() - loadStart
      logger.info({
        message: '📊 Transactions loaded',
        count: data.length,
        loadTimeMs: Math.round(loadTime),
        timestamp: new Date().toISOString(),
        filter: filter,
      })

      // 성능 경고: 로딩이 2초 이상 걸리면 최적화 권장
      if (loadTime > 2000) {
        logger.warn(
          `⚠️ 로딩이 느립니다 (${Math.round(loadTime)}ms). ` +
          `거래 건수: ${data.length}건. Phase 1 최적화를 고려하세요.`
        )
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('거래 목록을 불러오는데 실패했습니다'))
    } finally {
      setLoading(false)
    }
  }

  /**
   * 필터 변경 시 거래 목록 재조회
   *
   * @why 배열 필드(majorCategoryIds 등)를 의존성에 그대로 넣으면 부모가
   * 매 렌더마다 새 배열을 생성할 때 같은 내용에도 fetch가 반복된다.
   * 배열은 join('|')으로 직렬화하여 내용 기반 비교를 강제한다.
   * (값에 '|'가 들어갈 일이 없는 ID 배열이라 안전)
   */
  const arrayKey = (arr?: string[]) => (arr ? arr.join('|') : '')
  useEffect(() => {
    fetchTransactions()
  }, [
    filter?.startDate,
    filter?.endDate,
    filter?.type,
    filter?.majorCategoryId,
    filter?.minorCategoryId,
    arrayKey(filter?.majorCategoryIds),
    arrayKey(filter?.minorCategoryIds),
    filter?.spenderId,
    filter?.writerId,
    arrayKey(filter?.spenderIds),
    arrayKey(filter?.writerIds),
    filter?.paymentMethodId,
    arrayKey(filter?.paymentMethodIds),
    filter?.search,
  ])

  return {
    transactions,
    loading,
    error,
    refetch: fetchTransactions,
  }
}
